import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faKey,
  faLinkSlash,
  faPlus,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { partnerApi } from '../../services/api/partnerApi'
import RicMultiSelect from '../../components/partner/RicMultiSelect'
import GroupNameField from '../../components/partner/GroupNameField'
import ConfirmDeleteDialog from '../../components/admin/ConfirmDeleteDialog'
import type { PartnerCustomerDetail } from '../../types/partner'

interface UnassignTarget {
  deviceId: number
  wisolId: string
  groupName: string
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface AddDevicesPanelProps {
  customer: PartnerCustomerDetail
  onClose: () => void
  onSaved: () => void
}

function AddDevicesPanel({ customer, onClose, onSaved }: AddDevicesPanelProps) {
  const [deviceIds, setDeviceIds] = useState<number[]>([])
  const [groupChoice, setGroupChoice] = useState<number | 'new' | ''>(
    customer.groups.length === 1 ? customer.groups[0].locationId : '',
  )
  const [newGroupName, setNewGroupName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (deviceIds.length === 0) {
      setError('Pick at least one RIC')
      return
    }
    if (customer.groups.length > 1 && groupChoice === '') {
      setError('Choose a group')
      return
    }
    if (groupChoice === 'new' && !newGroupName.trim()) {
      setError('Enter a name for the new group')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const payload =
        groupChoice === 'new'
          ? { deviceIds, newGroupName: newGroupName.trim() }
          : groupChoice === ''
            ? { deviceIds }
            : { deviceIds, groupId: Number(groupChoice) }
      await partnerApi.addDevicesToCustomer(customer.user.userId, payload)
      onSaved()
    } catch (e: any) {
      setError(e?.message || 'Failed to add devices')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium">Add RICs</h2>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      {customer.groups.length === 0 ? (
        <GroupNameField
          value={newGroupName}
          onChange={(v) => {
            setGroupChoice('new')
            setNewGroupName(v)
          }}
          label="New group name (optional)"
          hint="This customer has no groups yet. Leave blank to auto-name."
        />
      ) : customer.groups.length === 1 ? (
        <p className="text-sm text-gray-600">
          Adding to <strong>{customer.groups[0].name}</strong>.
        </p>
      ) : (
        <>
          <label className="block text-sm font-medium mb-2">Add to group *</label>
          <select
            value={groupChoice}
            onChange={(e) => {
              const v = e.target.value
              setGroupChoice(v === '' ? '' : v === 'new' ? 'new' : Number(v))
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">— Select a group —</option>
            {customer.groups.map((g) => (
              <option key={g.locationId} value={g.locationId}>
                {g.name}
              </option>
            ))}
            <option value="new">+ Create a new group</option>
          </select>
          {groupChoice === 'new' && (
            <div className="mt-3">
              <GroupNameField
                value={newGroupName}
                onChange={setNewGroupName}
                label="New group name *"
                hint="Choose a name for the new site."
              />
            </div>
          )}
        </>
      )}

      <RicMultiSelect selectedDeviceIds={deviceIds} onSelectionChange={setDeviceIds} />

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Adding…' : 'Add devices'}
        </button>
      </div>
    </div>
  )
}

export default function CustomerDetail() {
  const { userId } = useParams<{ userId: string }>()
  const [customer, setCustomer] = useState<PartnerCustomerDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [unassignTarget, setUnassignTarget] = useState<UnassignTarget | null>(null)
  const [unassigning, setUnassigning] = useState(false)

  const refresh = async () => {
    if (!userId) return
    setLoading(true)
    setError(null)
    try {
      const detail = await partnerApi.getCustomer(Number(userId))
      setCustomer(detail)
    } catch (e: any) {
      setError(e?.message || 'Failed to load customer')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const handleUnassignConfirm = async () => {
    if (!customer || !unassignTarget) return
    setUnassigning(true)
    try {
      await partnerApi.unassignDeviceFromCustomer(customer.user.userId, unassignTarget.deviceId)
      setActionMessage(`RIC ${unassignTarget.wisolId} returned to unassigned inventory.`)
      setUnassignTarget(null)
      await refresh()
    } catch (e: any) {
      setActionMessage(e?.message || 'Failed to unassign device')
    } finally {
      setUnassigning(false)
    }
  }

  const handlePasswordReset = async () => {
    if (!customer) return
    setActionMessage(null)
    try {
      await partnerApi.sendPasswordReset(customer.user.userId)
      setActionMessage('Password reset email sent.')
    } catch (e: any) {
      setActionMessage(e?.message || 'Failed to send reset email')
    }
  }

  if (loading && !customer) {
    return <div className="p-6 text-gray-500">Loading…</div>
  }
  if (error || !customer) {
    return (
      <div className="p-6">
        <p className="text-red-600">{error || 'Customer not found'}</p>
        <Link to="/partner/customers" className="text-blue-600 hover:underline mt-3 inline-block">
          Back to customers
        </Link>
      </div>
    )
  }

  const u = customer.user
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email
  const totalDevices = customer.groups.reduce((sum, g) => sum + g.devices.length, 0)

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
      <div>
        <Link
          to="/partner/customers"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Customers
        </Link>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{fullName}</h1>
            <p className="text-sm text-gray-600">{u.email}</p>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
              <span>
                Status:{' '}
                <span
                  className={
                    u.status === 'active'
                      ? 'text-green-700 font-medium'
                      : 'text-yellow-700 font-medium'
                  }
                >
                  {u.status}
                </span>
              </span>
              <span>Joined: {formatDate(u.joinedAt)}</span>
              <span>Last login: {formatDate(u.lastLoginAt)}</span>
              <span>Logins: {u.loginCount}</span>
              <span>Devices: {totalDevices}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {u.status === 'active' && (
              <button
                type="button"
                onClick={handlePasswordReset}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
                title="Send a password reset email to the customer"
              >
                <FontAwesomeIcon icon={faKey} /> Send password reset
              </button>
            )}
          </div>
        </div>
        {actionMessage && (
          <p className="mt-3 text-sm text-blue-700">{actionMessage}</p>
        )}
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-medium">Devices</h2>
        {customer.groups.length === 0 && (
          <p className="text-sm text-gray-500">No devices yet. Use “Add RICs” below to assign devices to this customer.</p>
        )}
        {customer.groups.map((group) => (
          <div
            key={group.locationId}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden"
          >
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="font-medium">{group.name}</h3>
            </div>
            {group.devices.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-500">No RICs in this group.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                  <tr>
                    <th className="px-4 py-2 text-left">Sigfox ID</th>
                    <th className="px-4 py-2 text-left">Serial</th>
                    <th className="px-4 py-2 text-left">HW</th>
                    <th className="px-4 py-2 text-left">Registered</th>
                    <th className="px-4 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {group.devices.map((d) => (
                    <tr key={d.deviceId} className="border-t border-gray-100">
                      <td className="px-4 py-2 font-mono">{d.wisolId}</td>
                      <td className="px-4 py-2">{d.serial || '—'}</td>
                      <td className="px-4 py-2">{d.hardware}</td>
                      <td className="px-4 py-2">{d.sigfoxRegistered ? 'Y' : 'N'}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            setUnassignTarget({
                              deviceId: d.deviceId,
                              wisolId: d.wisolId,
                              groupName: group.name,
                            })
                          }
                          className="inline-flex items-center gap-1 text-xs text-red-700 hover:underline"
                          title="Return this RIC to your unassigned inventory"
                        >
                          <FontAwesomeIcon icon={faLinkSlash} /> Unassign
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      {showAdd ? (
        <AddDevicesPanel
          customer={customer}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false)
            setActionMessage('Devices added.')
            refresh()
          }}
        />
      ) : (
        <div>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            <FontAwesomeIcon icon={faPlus} /> Add RICs
          </button>
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!unassignTarget}
        onClose={() => (unassigning ? null : setUnassignTarget(null))}
        onConfirm={handleUnassignConfirm}
        title="Unassign RIC"
        message={
          unassignTarget
            ? `This will return RIC ${unassignTarget.wisolId} from “${unassignTarget.groupName}” to your unassigned inventory. ${fullName} will lose access to this device.`
            : ''
        }
        confirmText="UNASSIGN"
        confirmButtonText="Unassign"
        loadingText="Unassigning..."
        confirmButtonClassName="bg-red-600 text-white hover:bg-red-700"
        isLoading={unassigning}
      />
    </div>
  )
}
